/**
 * @file AccessListView.jsx
 * @author Fernando Ruano
 * @description Access View Page
 */

// Dependencies
import { useTranslation } from 'react-i18next';
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

// Helpers
import { useParams } from 'react-router';
import {
  joinArrayObject,
  removeNullValuesFromObject,
  sortStringPlus
} from 'commons/helpers/utils';
import {
  createColumns,
  getSearchApiKeys,
  getSelectFilter,
  getSorterString,
  PAGE_LIMIT
} from '../helper/utils';
import DashboardDetailList from '../dashboardDetailList';

function AccessListView({ dashboardStaffActions, clinicId }) {
  const { t: translate } = useTranslation();
  const [rowData, setRowData] = React.useState([]);
  const { classification, totalItems: totalItemsParams } = useParams();
  const [totalItems, setTotalItems] = useState(totalItemsParams);
  const [dataFilter, setDataFilter] = useState(null);
  const [columns, setColumns] = useState([]);
  const [filter, setFilter] = useState(null);
  const [sorter, setSorter] = useState(null);

  const { getAccessListPlus } = dashboardStaffActions;

  const additionalColumns = [
    {
      title: translate('dashboardStaff.treatmentOptions'),
      dataIndex: 'opciones_tratamiento',
      filter: {
        listName: 'lista_opciones_tratamiento',
        searchName: 'filtroOpcionesTratamiento',
        type: 'select'
      },
      sorter: {
        compare: sortStringPlus('opciones_tratamiento'),
        multiple: 16
      },
      ...getSelectFilter(
        {
          filter: {
            listName: 'lista_opciones_tratamiento',
            searchName: 'filtroOpcionesTratamiento'
          },
          dataIndex: 'opciones_tratamiento'
        },
        dataFilter
      ),
      ellipsis: true
    }
  ];

  const getetData = (offset, pageLimit) => {
    const pageSize = pageLimit || PAGE_LIMIT;
    const joinFilter = joinArrayObject(filter);
    const joinSorter = joinArrayObject(sorter);
    const data = {
      params: {
        controlSeleccionado: classification,
        idClinic: clinicId,
        limit: pageSize,
        offset,
        ...joinFilter,
        ...joinSorter
      }
    };
    getAccessListPlus(data, (_, resp) => {
      if (resp?.data && resp.data?.totalRegistros != null) {
        setTotalItems(resp.data?.totalRegistros);
        setRowData(resp.data?.listaDetalles ?? []);
        setDataFilter(resp.data);
      }
    });
  };

  useEffect(() => {
    if (clinicId !== null) getetData(1);
  }, [clinicId]);

  useEffect(() => {
    const cols = createColumns(translate, dataFilter);
    setColumns(cols);
  }, [rowData, dataFilter]);

  const handleTableChange = (pagination, filters, sorterFilter) => {
    if (filters || sorterFilter) {
      const convertedFilters = getSearchApiKeys(
        [...columns, ...additionalColumns],
        removeNullValuesFromObject(filters)
      );
      const convertedSorts = getSorterString(sorterFilter);
      setFilter(convertedFilters);
      setSorter(convertedSorts);
    }
  };

  return (
    <DashboardDetailList
      columns={columns}
      rowData={rowData}
      totalItems={totalItems}
      onPaginationChange={getetData}
      additionalColumns={additionalColumns}
      onChange={handleTableChange}
      filter={filter}
      sorter={sorter}
    />
  );
}

/**
 * Component definition of proptypes.
 */
AccessListView.propTypes = {
  dashboardStaffActions: PropTypes.shape({
    getAccessListPlus: PropTypes.func.isRequired
  }),
  clinicId: PropTypes.any
};

export default AccessListView;
